"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Montant,
    Question,
    Recommencer,
    Resultat,
    Segments,
    euros,
    remplir,
    useParcours,
} from "./Journey";
import {
    SALAIRE_UN_TRIMESTRE_2026,
    SEUIL_DIVIDENDES_EURL,
    meilleurPartage,
    partage,
    trimestresSalarie,
} from "@/lib/tools/salaryDividends";
import { expliquerPartage } from "@/lib/tools/explanations";
import { IndiceExplication, MontantExplicable, PanneauExplication, useExplications } from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Rémunération ou dividendes : combien me verser, et sous quelle forme ?
 *
 * LE PREMIER OUTIL DE LA RUBRIQUE QUI PARLE À QUELQU'UN QUI A DÉJÀ SA SOCIÉTÉ.
 * Les trente autres partent de la micro-entreprise. Le gérant d'EURL ou le
 * président de SASU, lui, se pose une question que la micro ne connaît pas :
 * ce que sa société gagne, il peut se le verser en rémunération, ou le laisser
 * payer l'impôt sur les sociétés puis se le distribuer. Le résultat n'a rien
 * d'évident, et il n'est pas le même dans les deux formes.
 *
 * LE RÉSULTAT MONTRE TOUS LES PARTAGES, PAS UN SEUL CHIFFRE. Une courbe va de
 * « tout en dividendes » à « tout en rémunération », le meilleur point y est
 * marqué, et un curseur promène le lecteur d'un partage à l'autre avec le
 * détail de ce que chacun coûte. On voit où la courbe est plate, c'est-à-dire
 * où le choix peut se faire sur autre chose que l'argent.
 *
 * ET IL DIT CE QUE L'ARGENT NE DIT PAS. En SASU, un euro de dividende ne cotise
 * à rien : l'écran compte les trimestres de retraite que chaque partage valide.
 *
 * TOUT RESTE DANS LE NAVIGATEUR : aucun appel réseau, aucun stockage.
 */

const PHASES = {
    forme: 0,
    impotRevenu: 0,
    activite: 0,
    cipav: 0,
    enveloppe: 1,
    capital: 1,
    foyer: 2,
    autresRevenus: 2,
    resultat: 3,
};

const L = {
    fr: {
        phases: ["Société", "Chiffres", "Foyer"],
        retour: "Retour",
        forme: {
            titre: "Quelle est votre société ?",
            aide: "En SASU, vos dividendes échappent aux cotisations. En EURL, presque tous en paient.",
            choix: [
                {
                    cle: "sasu",
                    icone: "user-id-linear",
                    titre: "Une SASU",
                    detail: "Vous en êtes le président. Pour la sécurité sociale, vous êtes considéré comme un salarié.",
                },
                {
                    cle: "eurl",
                    icone: "buildings-2-linear",
                    titre: "Une EURL à l'impôt sur les sociétés",
                    detail: "Vous en êtes le gérant et le seul associé. Vous cotisez comme un indépendant.",
                },
                {
                    cle: "eurlIr",
                    icone: "document-text-linear",
                    titre: "Une EURL à l'impôt sur le revenu",
                    detail: "Vous n'avez jamais choisi l'impôt sur les sociétés.",
                },
            ],
        },
        impotRevenu: {
            titre: "À l'impôt sur le revenu, vous n'avez pas encore ce choix",
            aide: "Votre EURL ne verse pas de dividendes : son bénéfice est imposé directement chez vous, comme celui d'une entreprise individuelle, que vous le laissiez dans la société ou non.",
            points: [
                "Le choix entre salaire et dividendes n'existe qu'une fois que vous avez opté pour l'impôt sur les sociétés.",
                "Pour savoir si cette option vaut le coup, regardez d'abord ce qu'elle changerait pour vous.",
            ],
            lien: "Voir ce que l'IS changerait",
        },
        activite: {
            titre: "Quelle est votre activité ?",
            aide: "Elle change une partie de vos cotisations d'indépendant.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Commerce ou prestations",
                    detail: "Vous vendez, ou vous rendez des services à des entreprises ou à des particuliers.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Artisanat",
                    detail: "Vous êtes immatriculé comme artisan.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Activité libérale",
                    detail: "Conseil, développement, design, formation, rédaction.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Profession réglementée",
                    detail: "Architecte, psychologue, ostéopathe : vous cotisez à la Cipav.",
                },
            ],
        },
        cipav: {
            titre: "Pour la Cipav, nous n'avons pas encore le calcul",
            aide: "À la Cipav, vous cotisez pour votre retraite selon un barème à part, que nous ne savons pas encore calculer au centime. Plutôt qu'un chiffre faux, nous préférons ne rien vous afficher.",
            points: [
                "La règle des 10 % vaut aussi pour vous : en EURL, vos dividendes paient des cotisations au-delà de 10 % de votre capital, de vos primes et de votre compte courant.",
                "Votre expert-comptable peut faire ce calcul avec les taux de la Cipav.",
            ],
        },
        enveloppe: {
            titre: "Combien votre société gagne-t-elle avant de vous payer ?",
            aide: "C'est son chiffre d'affaires moins ses dépenses, avant votre rémunération et vos cotisations. C'est cette somme que vous allez partager.",
            libelle: "Bénéfice avant votre rémunération",
            suivant: "Continuer",
        },
        capital: {
            titre: "Combien avez-vous mis dans votre société ?",
            aide: "Comptez le capital, les primes d'émission, et l'argent que vous avez laissé sur votre compte courant d'associé. Au-delà de {seuil} de ce montant, vos dividendes paient des cotisations, comme un salaire.",
            libelle: "Capital, primes et compte courant",
            seuil: "Vos dividendes paieront des cotisations au-delà de {montant}.",
            suivant: "Continuer",
        },
        foyer: {
            titre: "Qui compose votre foyer fiscal ?",
            aide: "Vos dividendes et votre rémunération s'ajoutent aux revenus de votre foyer, et votre impôt est calculé sur l'ensemble.",
            situation: "Situation",
            seul: "Seul",
            couple: "En couple",
            enfants: "Enfants à charge",
            suivant: "Continuer",
        },
        autresRevenus: {
            titre: "Y a-t-il d'autres revenus dans votre foyer ?",
            aide: "Un salaire, une pension, les revenus de votre conjoint. Nous ne comptons que l'impôt en plus que votre société vous fait payer.",
            libelle: "Autres revenus imposables du foyer",
            aucun: "Aucun autre revenu",
            suivant: "Trouver le bon partage",
        },
        resultat: {
            titre: "Votre partage",
            mixte: "Versez-vous {remuneration} de rémunération, et le reste en dividendes",
            toutDividendes: "C'est tout en dividendes qui vous laisse le plus",
            toutRemuneration: "Le mieux reste de tout vous verser en rémunération",
            gainDetail: "Il vous reste {net} par an, soit {gain} de plus que si vous vous versiez tout en salaire.",
            serreDetail:
                "Le meilleur partage ne vous rapporterait que {gain} de plus par an. En dessous de mille euros, ça ne vaut pas la peine de changer ce qui marche.",
            aucunGain: "Aucun autre partage ne vous laisse plus d'argent.",
            eurlPourquoi:
                "En EURL, vos dividendes paient des cotisations au-delà de {seuil}, en plus de l'impôt sur les sociétés. Au final, ils vous coûtent plus cher qu'un salaire.",
            courbe: "Ce qu'il vous reste, partage par partage",
            gauche: "Tout en dividendes",
            droite: "Tout en rémunération",
            meilleur: "le meilleur",
            explorer: "Part versée en rémunération",
            explorerValeur: "{part} %",
            lignes: {
                remunerationSasu: "Salaire brut",
                remunerationEurl: "Rémunération",
                cotisations: "Cotisations sur la rémunération",
                impotSocietes: "Impôt sur les sociétés",
                dividendes: "Dividendes",
                cotisationsDividendes: "Cotisations sur les dividendes",
                prelevementsSociaux: "Prélèvements sociaux",
                impotRevenu: "Impôt sur le revenu",
                reste: "Il vous reste",
            },
            pfu: "Vos dividendes paient le prélèvement forfaitaire de 31,4 % : pour votre foyer, c'est le moins cher.",
            bareme: "Vos dividendes sont imposés au barème, après un abattement de 40 % : pour votre foyer, c'est moins cher que le prélèvement forfaitaire. Ce choix vaut pour tous vos revenus de placement de l'année.",
            protection: {
                titre: "Et votre retraite ?",
                sasuAucun:
                    "Sans salaire, votre SASU ne vous fait valider aucun trimestre de retraite. Et si vous tombez malade, vous n'avez droit à aucune indemnité journalière.",
                sasuTrimestres: {
                    aucun: "Avec {brut} bruts dans l'année, vous ne validez aucun trimestre de retraite.",
                    un: "Avec {brut} bruts dans l'année, vous validez un trimestre de retraite sur quatre.",
                    plusieurs: "Avec {brut} bruts dans l'année, vous validez {n} trimestres de retraite sur quatre.",
                },
                sasuSeuil: "Il vous faut {seuil} bruts pour valider les quatre.",
                quatre: "Pour valider vos quatre trimestres, il vous faut {brut} de salaire brut. Ce partage vous laisse {cout} de moins par an que le meilleur.",
                quatreLibre: "Pour valider vos quatre trimestres, il vous faut {brut} de salaire brut, et ça ne vous coûte rien par rapport au meilleur partage.",
                voir: "Essayer ce partage",
                eurl: "En EURL, vos cotisations vous ouvrent des droits, qu'elles portent sur votre rémunération ou sur vos dividendes au-delà de {seuil}. Le partage change surtout ce que vous payez.",
            },
            hypotheses: {
                titre: "Pour ce calcul, nous avons supposé que :",
                items: [
                    "tout le bénéfice qui reste après l'impôt sur les sociétés vous est versé dans l'année ;",
                    "en EURL, vous payez vous-même les cotisations dues sur vos dividendes, sans les déduire nulle part. C'est le choix le plus prudent ;",
                    "en SASU, la cotisation accidents du travail n'est pas comptée, parce que son taux est fixé pour chaque entreprise ;",
                    "l'impôt compté est celui que votre société ajoute à votre foyer, pas tout l'impôt du foyer.",
                ],
            },
            avertissement:
                "Avant de changer quoi que ce soit, faites vérifier ce partage par votre expert-comptable sur vos vrais comptes, et pensez à votre retraite : sur vingt ans, elle pèse autant que l'écart affiché ici.",
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Company", "Figures", "Household"],
        retour: "Back",
        forme: {
            titre: "What is your company?",
            aide: "In a SASU, your dividends escape contributions. In an EURL, almost none do.",
            choix: [
                {
                    cle: "sasu",
                    icone: "user-id-linear",
                    titre: "A SASU",
                    detail: "You are its president. For social security, you count as an employee.",
                },
                {
                    cle: "eurl",
                    icone: "buildings-2-linear",
                    titre: "An EURL under corporate tax",
                    detail: "You are its manager and only shareholder. You pay contributions as a self-employed person.",
                },
                {
                    cle: "eurlIr",
                    icone: "document-text-linear",
                    titre: "An EURL under income tax",
                    detail: "You have never chosen corporate tax.",
                },
            ],
        },
        impotRevenu: {
            titre: "Under income tax, you do not have this choice yet",
            aide: "Your EURL pays no dividends: its profit is taxed directly in your hands, like a sole trader's, whether you leave it in the company or not.",
            points: [
                "The choice between salary and dividends only exists once you have opted for corporate tax.",
                "To know whether that option is worth it, first look at what it would change for you.",
            ],
            lien: "See what corporate tax would change",
        },
        activite: {
            titre: "What is your activity?",
            aide: "It changes part of your self-employed contributions.",
            choix: [
                {
                    cle: "services",
                    icone: "shop-linear",
                    titre: "Trade or services",
                    detail: "You sell goods, or provide services to businesses or individuals.",
                },
                {
                    cle: "artisan",
                    icone: "settings-minimalistic-linear",
                    titre: "Craft trade",
                    detail: "You are registered as a craftsperson.",
                },
                {
                    cle: "bnc",
                    icone: "pen-new-square-linear",
                    titre: "Liberal profession",
                    detail: "Consulting, development, design, training, writing.",
                },
                {
                    cle: "cipav",
                    icone: "stethoscope-linear",
                    titre: "Regulated profession",
                    detail: "Architect, psychologist, osteopath: you contribute to the Cipav.",
                },
            ],
        },
        cipav: {
            titre: "We do not have the Cipav calculation yet",
            aide: "With the Cipav, your pension contributions follow their own scale, which we cannot yet compute to the cent. Rather than show you a wrong figure, we prefer to show nothing.",
            points: [
                "The 10% rule applies to you too: in an EURL, your dividends carry contributions beyond 10% of your capital, premiums and current account.",
                "Your accountant can run this calculation with the Cipav rates.",
            ],
        },
        enveloppe: {
            titre: "How much does your company make before paying you?",
            aide: "That is its turnover minus its expenses, before your pay and your contributions. It is the amount you are about to split.",
            libelle: "Profit before your pay",
            suivant: "Continue",
        },
        capital: {
            titre: "How much have you put into your company?",
            aide: "Count the share capital, share premiums, and the money you have left in your shareholder current account. Beyond {seuil} of that amount, your dividends carry contributions, just like a salary.",
            libelle: "Capital, premiums and current account",
            seuil: "Your dividends will carry contributions beyond {montant}.",
            suivant: "Continue",
        },
        foyer: {
            titre: "Who is in your tax household?",
            aide: "Your dividends and your pay add to your household's income, and your tax is calculated on the whole.",
            situation: "Situation",
            seul: "Single",
            couple: "Couple",
            enfants: "Dependent children",
            suivant: "Continue",
        },
        autresRevenus: {
            titre: "Is there other income in your household?",
            aide: "A salary, a pension, your partner's income. We only count the extra tax your company makes you pay.",
            libelle: "Other taxable household income",
            aucun: "No other income",
            suivant: "Find the right split",
        },
        resultat: {
            titre: "Your split",
            mixte: "Pay yourself {remuneration} in salary, and the rest in dividends",
            toutDividendes: "All in dividends leaves you the most",
            toutRemuneration: "The best is still to take everything as pay",
            gainDetail: "You keep {net} a year, that is {gain} more than if you took everything as salary.",
            serreDetail:
                "The best split would only give you {gain} more a year. Below a thousand euros, it is not worth changing what works.",
            aucunGain: "No other split leaves you more money.",
            eurlPourquoi:
                "In an EURL, your dividends carry contributions beyond {seuil}, on top of corporate tax. In the end, they cost you more than pay.",
            courbe: "What you keep, split by split",
            gauche: "All in dividends",
            droite: "All in pay",
            meilleur: "the best",
            explorer: "Share paid as salary",
            explorerValeur: "{part}%",
            lignes: {
                remunerationSasu: "Gross salary",
                remunerationEurl: "Pay",
                cotisations: "Contributions on pay",
                impotSocietes: "Corporate tax",
                dividendes: "Dividends",
                cotisationsDividendes: "Contributions on dividends",
                prelevementsSociaux: "Social levies",
                impotRevenu: "Income tax",
                reste: "You keep",
            },
            pfu: "Your dividends pay the 31.4% flat tax: for your household, it is the cheapest.",
            bareme: "Your dividends are taxed on the scale, after a 40% allowance: for your household, that is cheaper than the flat tax. This choice covers all your investment income for the year.",
            protection: {
                titre: "And your pension?",
                sasuAucun:
                    "With no salary, your SASU earns you no pension quarter. And if you fall ill, you have no right to sick pay.",
                sasuTrimestres: {
                    aucun: "With {brut} gross in the year, you validate no pension quarter.",
                    un: "With {brut} gross in the year, you validate one pension quarter out of four.",
                    plusieurs: "With {brut} gross in the year, you validate {n} pension quarters out of four.",
                },
                sasuSeuil: "You need {seuil} gross to validate all four.",
                quatre: "To validate your four quarters, you need {brut} of gross salary. That split leaves you {cout} less a year than the best one.",
                quatreLibre: "To validate your four quarters, you need {brut} of gross salary, and it costs you nothing compared with the best split.",
                voir: "Try that split",
                eurl: "In an EURL, your contributions earn you rights, whether they fall on your pay or on your dividends beyond {seuil}. The split mostly changes what you pay.",
            },
            hypotheses: {
                titre: "For this calculation, we assumed that:",
                items: [
                    "all the profit left after corporate tax is paid out to you within the year;",
                    "in an EURL, you pay the contributions due on your dividends yourself, without deducting them anywhere. It is the most cautious choice;",
                    "in a SASU, the workplace accident contribution is not counted, because its rate is set for each company;",
                    "the tax counted is what your company adds to your household, not the household's whole tax.",
                ],
            },
            avertissement:
                "Before changing anything, have your accountant check this split on your real accounts, and think of your pension: over twenty years, it weighs as much as the gap shown here.",
            recommencer: "Start again",
        },
    },
};

/**
 * La courbe de tous les partages, du tout-dividendes au tout-rémunération.
 *
 * Un tracé, deux points : le meilleur, et celui que le curseur explore. Pas
 * d'axe gradué, qui ferait lire des montants que le détail donne déjà au-dessous :
 * la courbe dit seulement où elle monte, où elle est plate, où elle culmine.
 */
function Courbe({ points, meilleur, explore, textes }) {
    const L = 320;
    const H = 110;
    const marge = 8;
    const nets = points.map((p) => p.net);
    const bas = Math.min(...nets);
    const haut = Math.max(...nets);
    const etendue = haut - bas || 1;
    const x = (part) => marge + part * (L - 2 * marge);
    const y = (net) => H - marge - ((net - bas) / etendue) * (H - 2 * marge - 14);
    const trace = points.map((p, i) => `${i ? "L" : "M"}${x(p.part).toFixed(1)},${y(p.net).toFixed(1)}`).join(" ");

    return (
        <div className="mt-6">
            <div className="text-sm font-semibold dark:text-white mb-2">{textes.courbe}</div>
            <svg viewBox={`0 0 ${L} ${H}`} className="w-full h-auto" aria-hidden="true">
                <path d={trace} fill="none" stroke="currentColor" strokeWidth="2" className="text-primary" />
                <line
                    x1={x(explore.part)}
                    x2={x(explore.part)}
                    y1={marge}
                    y2={H - marge}
                    stroke="currentColor"
                    strokeDasharray="3 3"
                    className="text-gray-300 dark:text-white/20"
                />
                <circle cx={x(explore.part)} cy={y(explore.net)} r="4" className="fill-gray-900 dark:fill-white" />
                <circle cx={x(meilleur.part)} cy={y(meilleur.net)} r="5" className="fill-primary" />
                <text
                    x={Math.min(Math.max(x(meilleur.part), 30), L - 30)}
                    y={Math.max(y(meilleur.net) - 9, 10)}
                    textAnchor="middle"
                    className="fill-primary text-[10px] font-semibold"
                >
                    {textes.meilleur}
                </text>
            </svg>
            <div className="flex justify-between text-[11px] text-gray-500 dark:text-lightBlue/70">
                <span>{textes.gauche}</span>
                <span>{textes.droite}</span>
            </div>
        </div>
    );
}

export default function RemunerationOuDividendes({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // L'EURL à l'IR et la CIPAV ne sont jamais des valeurs de l'outil : ce
    // sont des écrans de refus, où la reprise mène directement.
    const { valeurs: dossier } = useDossierOutil();
    const formeDossier = dossier.forme === "eurl" || dossier.forme === "sasu" ? dossier.forme : undefined;
    const activiteDossier = dossier.activite !== "cipav" ? dossier.activite : undefined;
    // Un bénéfice sous le minimum du curseur ne se reprend pas : la question reste.
    const enveloppeDossier = dossier.enveloppe >= 5000 ? dossier.enveloppe : undefined;
    const [forme, setForme] = useState(formeDossier ?? null);
    const [activite, setActivite] = useState(activiteDossier ?? null);
    const [enveloppe, setEnveloppe] = useState(enveloppeDossier ?? 60000);
    const [capital, setCapital] = useState(1000);
    const [enCouple, setEnCouple] = useState(dossier.enCouple ?? false);
    const [enfants, setEnfants] = useState(dossier.enfants ?? 0);
    const [autresRevenus, setAutresRevenus] = useState(dossier.autresRevenus ?? 0);
    // Le partage exploré au curseur, en points de pourcentage ; null tant que
    // le lecteur n'y a pas touché, et le curseur se pose alors sur le meilleur.
    const [explore, setExplore] = useState(null);

    const { etape, communs, rang, aller, retour, recommencer, reduit } = useParcours(
        "forme",
        () => {
            setForme(null);
            setActivite(null);
            setEnveloppe(60000);
            setCapital(1000);
            setEnCouple(false);
            setEnfants(0);
            setAutresRevenus(0);
            setExplore(null);
        },
        PHASES,
        reprendre(
            dossier.forme === "eurlIr"
                ? [{ etape: "forme", connu: true }, { etape: "impotRevenu" }]
                : dossier.forme === "eurl" && dossier.activite === "cipav"
                  ? [
                        { etape: "forme", connu: true },
                        { etape: "activite", connu: true },
                        { etape: "cipav" },
                    ]
                  : [
                        { etape: "forme", connu: formeDossier !== undefined },
                        ...(formeDossier === "eurl"
                            ? [{ etape: "activite", connu: activiteDossier !== undefined }]
                            : []),
                        { etape: "enveloppe", connu: enveloppeDossier !== undefined },
                        // Le capital social ne se déduit pas du dossier : le parcours s'y arrête.
                        ...(formeDossier === "eurl" ? [{ etape: "capital", connu: false }] : []),
                        { etape: "foyer", connu: dossier.enCouple !== undefined && dossier.enfants !== undefined },
                        { etape: "autresRevenus", connu: dossier.autresRevenus !== undefined },
                        { etape: "resultat" },
                    ],
        ),
    );

    useSignalerDossier({ forme, activite, enCouple, enfants, autresRevenus }, etape === "resultat");

    const parametres = useMemo(
        () => ({
            forme,
            enveloppe,
            capital,
            activite: activite === "artisan" ? "services" : activite || "bnc",
            artisan: activite === "artisan",
            foyer: { enCouple, enfants, autresRevenus },
        }),
        [forme, enveloppe, capital, activite, enCouple, enfants, autresRevenus],
    );

    const calcul = useMemo(
        () => (etape === "resultat" ? meilleurPartage(parametres) : null),
        [etape, parametres],
    );

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(Math.round(v), lang);
    // Un seul calcul déplié à la fois, sous la ligne cliquée.
    const { ouverte, basculer } = useExplications();

    if (etape === "forme") {
        return (
            <Question
                key="forme"
                {...communsEcran}
                phase={PHASES.forme}
                rang={rang}
                titre={t.forme.titre}
                aide={t.forme.aide}
                choix={t.forme.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        setExplore(null);
                        if (c.cle === "eurlIr") {
                            aller("impotRevenu");
                            return;
                        }
                        setForme(c.cle);
                        // Le président de SASU cotise comme un cadre, quelle que
                        // soit son activité : la question ne se pose qu'en EURL.
                        aller(c.cle === "eurl" ? "activite" : "enveloppe");
                    },
                }))}
            />
        );
    }

    if (etape === "impotRevenu" || etape === "cipav") {
        // Deux refus motivés : l'écran dit pourquoi il s'arrête, et ce qui
        // reste vrai pour le lecteur, plutôt que d'afficher un chiffre faux.
        const texte = etape === "impotRevenu" ? t.impotRevenu : t.cipav;
        return (
            <Ecran
                key={etape}
                {...communsEcran}
                phase={PHASES[etape]}
                rang={rang}
                titre={texte.titre}
                aide={texte.aide}
            >
                <Liste items={texte.points} />
                {etape === "impotRevenu" && (
                    // Le bouton porte la couleur, hors de portée de la règle
                    // globale a:hover qui repeint en rose tout lien coloré.
                    <div className="mt-5">
                        <Link
                            href={{
                                pathname: "/tools/[slug]",
                                params: {
                                    slug:
                                        lang === "fr"
                                            ? "passer-a-l-impot-sur-les-societes"
                                            : "switch-to-corporate-tax",
                                },
                            }}
                        >
                            <Button type="primary" className="press-safe w-full">
                                {t.impotRevenu.lien} →
                            </Button>
                        </Link>
                    </div>
                )}
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "activite") {
        return (
            <Question
                key="activite"
                {...communsEcran}
                phase={PHASES.activite}
                rang={rang}
                titre={t.activite.titre}
                aide={t.activite.aide}
                choix={t.activite.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "cipav") {
                            aller("cipav");
                            return;
                        }
                        setActivite(c.cle);
                        aller("enveloppe");
                    },
                }))}
            />
        );
    }

    if (etape === "enveloppe") {
        return (
            <Ecran
                key="enveloppe"
                {...communsEcran}
                phase={PHASES.enveloppe}
                rang={rang}
                titre={t.enveloppe.titre}
                aide={t.enveloppe.aide}
            >
                <Curseur
                    libelle={t.enveloppe.libelle}
                    valeur={enveloppe}
                    poser={setEnveloppe}
                    minimum={5000}
                    maximum={300000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller(forme === "eurl" ? "capital" : "foyer")}>
                    {t.enveloppe.suivant}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "capital") {
        return (
            <Ecran
                key="capital"
                {...communsEcran}
                phase={PHASES.capital}
                rang={rang}
                titre={t.capital.titre}
                aide={remplir(t.capital.aide, { seuil: `${SEUIL_DIVIDENDES_EURL * 100} %` })}
            >
                <Curseur
                    libelle={t.capital.libelle}
                    aide={remplir(t.capital.seuil, {
                        montant: sommes(capital * SEUIL_DIVIDENDES_EURL),
                    })}
                    valeur={capital}
                    poser={setCapital}
                    minimum={0}
                    maximum={100000}
                    saisie
                    pas={500}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("foyer")}>{t.capital.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "foyer") {
        return (
            <Ecran
                key="foyer"
                {...communsEcran}
                phase={PHASES.foyer}
                rang={rang}
                titre={t.foyer.titre}
                aide={t.foyer.aide}
            >
                <div className="space-y-5">
                    <div>
                        <div id="remuneration-foyer-situation" className="text-sm text-gray-600 dark:text-lightBlue mb-2">
                            {t.foyer.situation}
                        </div>
                        <Segments
                            labelledBy="remuneration-foyer-situation"
                            options={[
                                { cle: "seul", libelle: t.foyer.seul },
                                { cle: "couple", libelle: t.foyer.couple },
                            ]}
                            valeur={enCouple ? "couple" : "seul"}
                            poser={(v) => setEnCouple(v === "couple")}
                        />
                    </div>
                    <Curseur
                        libelle={t.foyer.enfants}
                        valeur={enfants}
                        poser={setEnfants}
                        minimum={0}
                        maximum={6}
                        pas={1}
                        formater={(v) => String(v)}
                    />
                </div>
                <Avancer onClick={() => aller("autresRevenus")}>{t.foyer.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "autresRevenus") {
        return (
            <Ecran
                key="autresRevenus"
                {...communsEcran}
                phase={PHASES.autresRevenus}
                rang={rang}
                titre={t.autresRevenus.titre}
                aide={t.autresRevenus.aide}
            >
                <Curseur
                    libelle={t.autresRevenus.libelle}
                    aide={autresRevenus === 0 ? t.autresRevenus.aucun : undefined}
                    valeur={autresRevenus}
                    poser={setAutresRevenus}
                    minimum={0}
                    maximum={120000}
                    saisie
                    pas={1000}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("resultat")}>{t.autresRevenus.suivant}</Avancer>
            </Ecran>
        );
    }

    const { meilleur, gain, partageUtile } = calcul;
    const partExploree = explore === null ? Math.round(meilleur.part * 100) : explore;
    const point =
        explore === null ? meilleur : partage({ ...parametres, part: partExploree / 100 });
    const seuilDividendes = sommes(capital * SEUIL_DIVIDENDES_EURL);

    // LE TITRE RÉPOND À LA QUESTION POSÉE : combien me verser en rémunération.
    // Sous mille euros d'écart avec « tout en rémunération », le réflexe de qui
    // sort de la micro, l'écran refuse de faire bouger le lecteur.
    const titre = !partageUtile
        ? r.toutRemuneration
        : meilleur.part < 0.005
          ? r.toutDividendes
          : remplir(r.mixte, {
                remuneration: sommes(
                    forme === "sasu" ? meilleur.remunerationBrute : meilleur.remunerationAvantImpot,
                ),
            });

    const lignes = [
        [
            forme === "sasu" ? r.lignes.remunerationSasu : r.lignes.remunerationEurl,
            forme === "sasu" ? point.remunerationBrute : point.remunerationAvantImpot,
            "remuneration",
        ],
        [r.lignes.cotisations, point.cotisationsRemuneration, "cotisations"],
        [r.lignes.impotSocietes, point.impotSocietes, "impotSocietes"],
        [r.lignes.dividendes, point.dividendes, "dividendes"],
        forme === "eurl" && [r.lignes.cotisationsDividendes, point.cotisationsDividendes, "cotisationsDividendes"],
        [r.lignes.prelevementsSociaux, point.prelevementsSociaux, "prelevementsSociaux"],
        [r.lignes.impotRevenu, point.impotRevenu, "impotRevenu"],
    ].filter(Boolean);
    // Chaque ligne dit d'où elle vient : ses étapes et ses sources.
    const ex = expliquerPartage(parametres, point, lang);

    const trimestres = trimestresSalarie(point.remunerationBrute);

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">{titre}</div>
            <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                {partageUtile
                    ? remplir(r.gainDetail, { net: sommes(meilleur.net), gain: sommes(gain) })
                    : gain < 1
                      ? r.aucunGain
                      : remplir(r.serreDetail, { gain: sommes(gain) })}
            </p>
            {forme === "eurl" && !partageUtile && (
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                    {remplir(r.eurlPourquoi, { seuil: seuilDividendes })}
                </p>
            )}

            <Courbe points={calcul.points} meilleur={meilleur} explore={point} textes={r} />

            <div className="mt-5">
                <Curseur
                    libelle={r.explorer}
                    valeur={partExploree}
                    poser={setExplore}
                    minimum={0}
                    maximum={100}
                    pas={1}
                    formater={(v) => remplir(r.explorerValeur, { part: v })}
                />
            </div>

            {/* LE DÉTAIL DU PARTAGE EXPLORÉ, ligne par ligne : ce que chaque euro
                traverse avant d'arriver sur le compte du dirigeant. */}
            <IndiceExplication lang={lang} />
            <div className="mt-3 rounded-xl border border-gray-200/70 dark:border-white/10 divide-y divide-gray-200/70 dark:divide-white/10">
                {lignes.map(([libelle, valeur, cle]) => (
                    <div key={cle} className="px-4 py-2.5 text-sm">
                        <div className="flex items-baseline justify-between gap-4">
                            <span className="text-gray-600 dark:text-lightBlue">{libelle}</span>
                            <span className="tabular-nums dark:text-white">
                                <MontantExplicable explication={ex[cle]} ouvert={ouverte === cle} onBasculer={() => basculer(cle)}>
                                    {sommes(valeur)}
                                </MontantExplicable>
                            </span>
                        </div>
                        {ouverte === cle && <PanneauExplication explication={ex[cle]} lang={lang} />}
                    </div>
                ))}
                <div className="px-4 py-3 bg-primary/[0.06]">
                    <div className="flex items-baseline justify-between gap-4">
                        <span className="font-semibold dark:text-white">{r.lignes.reste}</span>
                        <span className="font-bold tabular-nums dark:text-white">
                            <MontantExplicable explication={ex.reste} ouvert={ouverte === "reste"} onBasculer={() => basculer("reste")}>
                                <Montant valeur={Math.max(0, Math.round(point.net))} lang={lang} reduit={reduit} />
                            </MontantExplicable>
                        </span>
                    </div>
                    {ouverte === "reste" && <PanneauExplication explication={ex.reste} lang={lang} />}
                </div>
            </div>
            {point.dividendes > 0 && (
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-2 mb-0">
                    {point.imposition === "bareme" ? r.bareme : r.pfu}
                </p>
            )}

            <div className="mt-6 rounded-xl bg-gray-50 dark:bg-white/[0.04] px-4 py-4">
                <div className="flex items-center gap-2 font-semibold dark:text-white">
                    <SolarIcon name="shield-user-linear" className="w-5 h-5 text-primary" />
                    {r.protection.titre}
                </div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                    {forme === "eurl"
                        ? remplir(r.protection.eurl, { seuil: seuilDividendes })
                        : trimestres === 0 && point.remunerationBrute < 1
                          ? r.protection.sasuAucun
                          : `${remplir(
                                trimestres === 0
                                    ? r.protection.sasuTrimestres.aucun
                                    : trimestres === 1
                                      ? r.protection.sasuTrimestres.un
                                      : r.protection.sasuTrimestres.plusieurs,
                                { brut: sommes(point.remunerationBrute), n: trimestres },
                            )}${
                                trimestres < 4
                                    ? ` ${remplir(r.protection.sasuSeuil, {
                                          seuil: sommes(4 * SALAIRE_UN_TRIMESTRE_2026),
                                      })}`
                                    : ""
                            }`}
                </p>
                {/* LA RETRAITE D'UNE ANNÉE, CHIFFRÉE PLUTÔT QUE SERMONNÉE. Le
                    lecteur voit qu'il gagne plus sans salaire ; l'écran lui
                    montre ce que coûtent ses quatre trimestres, et l'y emmène
                    d'un clic. */}
                {forme === "sasu" && trimestres < 4 && calcul.quatreTrimestres && (
                    <>
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {remplir(
                                calcul.quatreTrimestres.cout >= 1
                                    ? r.protection.quatre
                                    : r.protection.quatreLibre,
                                {
                                    brut: sommes(calcul.quatreTrimestres.brut),
                                    cout: sommes(calcul.quatreTrimestres.cout),
                                },
                            )}
                        </p>
                        <button
                            type="button"
                            onClick={() =>
                                // Arrondi au point supérieur : un point de moins
                                // retomberait sous le salaire des quatre trimestres.
                                setExplore(Math.ceil(calcul.quatreTrimestres.point.part * 100))
                            }
                            className="mt-2 text-sm font-semibold text-primary hover:underline underline-offset-4"
                        >
                            {r.protection.voir} →
                        </button>
                    </>
                )}
            </div>

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste items={r.hypotheses.items} />
            </div>

            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-6 mb-0 leading-relaxed">
                {r.avertissement}
            </p>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
